/**
 * Index Page (Homepage)
 * 
 * CRITICAL: All live match data is read from the centralized useLiveMatchStore.
 * This ensures consistent live scores, minutes, and status everywhere.
 * Never independently fetch or manage live state per component.
 */

import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { useSearchParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { getRealWorldDate } from '@/lib/utils';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { DateSelector } from '@/components/DateSelector';
import { VirtualizedMatchList } from '@/components/VirtualizedMatchList';
import { MatchListSkeleton } from '@/components/MatchListSkeleton';
import { SEOHead, generateWebsiteJsonLd } from '@/components/SEOHead';
import { ErrorFallback } from '@/components/ErrorFallback';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { GlobalCTAButtons } from '@/components/GlobalCTAButtons';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, Calendar, Zap, HelpCircle, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLiveMatchStore } from '@/hooks/useLiveMatchStore';
import { useMatchesForDate } from '@/hooks/useMatchesForDate';
import { useAutoRetry } from '@/hooks/useAutoRetry';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { classifyError } from '@/lib/errorHandling';
import { WelcomeBanner, HowItWorksModal } from '@/components/onboarding';
import { preloadImages, cleanupImagePreloads } from '@/lib/imageOptimization';

export default function Index() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(getRealWorldDate());
  const [filter, setFilter] = useState<'all' | 'live'>('all');
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  // Handle referral code from URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode && user) {
      // Process referral after user signs up
      const processReferral = async () => {
        const { data, error } = await supabase.rpc('process_referral_signup', {
          p_referral_code: refCode,
        });
        
        if (!error && data) {
          const result = data as { success: boolean; message: string };
          if (result.success) {
            toast.success('Referral registered! Complete predictions to reward your friend.');
          }
        }
        
        // Remove ref from URL after processing
        searchParams.delete('ref');
        setSearchParams(searchParams, { replace: true });
      };
      
      processReferral();
    } else if (refCode && !user) {
      // Store in localStorage to process after signup
      localStorage.setItem('pendingReferralCode', refCode);
    }
  }, [user, searchParams, setSearchParams]);

  // Optimized queries with proper caching
  const { 
    data: dateData, 
    isLoading: isLoadingDate, 
    error: dateError, 
    refetch: refetchDate, 
    isFetching: isFetchingDate 
  } = useMatchesForDate(selectedDate, filter === 'all');

  // Use centralized live match store (single source of truth)
  const { 
    allLiveMatches: liveMatches, 
    isLoading: isLoadingLive, 
    isPolling: isFetchingLive,
    liveCount,
    refresh: refetchLive 
  } = useLiveMatchStore();

  // Derive leagues from live matches
  const liveLeagues = useMemo(() => {
    if (!liveMatches) return [];
    const leagueMap = new Map();
    liveMatches.forEach(m => {
      if (m.league) {
        leagueMap.set(m.league.id, m.league);
      }
    });
    return Array.from(leagueMap.values()).sort((a, b) => b.priority - a.priority);
  }, [liveMatches]);

  // Select data based on active filter
  const matches = filter === 'live' ? (liveMatches ?? []) : (dateData?.matches ?? []);
  const leagues = filter === 'live' ? liveLeagues : (dateData?.leagues ?? []);
  const isLoading = filter === 'live' ? isLoadingLive : isLoadingDate;
  const error = filter === 'live' ? null : dateError; // Live store handles its own errors
  const refetch = filter === 'live' ? refetchLive : refetchDate;
  const isFetching = filter === 'live' ? isFetchingLive : isFetchingDate;

  // Auto-retry logic for match loading failures
  const {
    countdownSeconds,
    retryCount,
    isExhausted,
    manualRetry,
  } = useAutoRetry({
    isError: !!error,
    onRetry: async () => { await refetch(); },
    isFetching,
    maxRetries: 3,
    baseDelay: 3000,
  });

  // Preload first visible team logos for faster LCP
  // Clean up when component unmounts to avoid stale preloads
  useEffect(() => {
    if (matches.length > 0) {
      const firstFewMatches = matches.slice(0, 6);
      const logoUrls = firstFewMatches.flatMap(m => [m.home_team_logo, m.away_team_logo]);
      preloadImages(logoUrls, 'high');
    }
    
    // Cleanup preloads when navigating away
    return () => {
      cleanupImagePreloads();
    };
  }, [matches]);

  // Generate structured data for home page
  const websiteJsonLd = generateWebsiteJsonLd();

  // Determine error type for better UX
  const getErrorType = (): 'network' | 'server' | 'generic' => {
    if (!error) return 'generic';
    const appError = classifyError(error);
    if (appError.category === 'network') return 'network';
    if (appError.category === 'api') return 'server';
    return 'generic';
  };

  return (
    <div id="page-top" className="min-h-[100dvh] bg-background flex flex-col">
      {/* SEO Head for Home Page */}
      <SEOHead
        title="WinWGo - Expert Football Predictions & Betting Tips"
        description="Get expert football predictions and analysis for Premier League, La Liga, Serie A, and top leagues worldwide. AI-powered betting tips and match insights."
        canonical="https://winwgo.com"
        jsonLd={websiteJsonLd}
      />
      
      <Header />
      
      <main className="container mx-auto px-2.5 sm:px-6 py-3 sm:py-6 max-w-4xl flex-1">
        {/* Welcome Banner for first-time visitors */}
        <WelcomeBanner onShowHowItWorks={() => setShowHowItWorks(true)} />

        {/* Hero Section - Compact on mobile */}
        <header id="predictions-header" className="mb-3 sm:mb-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg bg-primary/10">
                <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-primary" aria-hidden="true" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground leading-tight">
                  Football Predictions
                </h1>
                <p className="text-xs sm:text-sm text-muted-foreground">AI-powered tips</p>
              </div>
            </div>
            
            {/* How it works button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHowItWorks(true)}
              className="gap-1 h-8 sm:h-9 px-2 sm:px-2.5 text-[10px] sm:text-xs font-medium"
            >
              <HelpCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              <span className="hidden sm:inline">How it works</span>
              <span className="sm:hidden">Help</span>
            </Button>
          </div>
        </header>

        {/* Filter Tabs - More compact */}
        <Tabs value={filter} onValueChange={(v) => setFilter(v as 'all' | 'live')} className="mb-3 sm:mb-5">
          <TabsList className="grid w-full max-w-[200px] sm:max-w-xs mx-auto grid-cols-2 h-8 sm:h-10 p-0.5">
            <TabsTrigger value="all" className="gap-1 text-[10px] sm:text-sm font-medium">
              <Calendar className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              All
            </TabsTrigger>
            <TabsTrigger value="live" className="gap-1 text-[10px] sm:text-sm font-medium">
              <Zap className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              Live
              {liveCount > 0 && (
                <span className="ml-0.5 px-1 py-0.5 text-[8px] sm:text-[9px] font-bold rounded-full bg-destructive text-destructive-foreground tabular-nums">
                  {liveCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Date Selector - only show for "all" filter */}
        {filter === 'all' && (
          <div className="mb-3 sm:mb-5">
            <DateSelector selectedDate={selectedDate} onDateChange={setSelectedDate} />
          </div>
        )}

        {/* Section Divider - subtle on mobile */}
        <div className="h-px bg-border/30 mb-3 sm:mb-5" />

        {/* Matches List */}
        <section id="matches-section">
          <ErrorBoundary componentName="MatchList">
            {error ? (
              <ErrorFallback
                type={getErrorType()}
                title="Unable to load matches"
                message={
                  filter === 'live'
                    ? "We're having trouble loading live matches. Please try again."
                    : `We're having trouble loading matches for ${format(selectedDate, 'MMMM d')}. Please try again.`
                }
                onRetry={manualRetry}
                isRetrying={isFetching}
                countdownSeconds={countdownSeconds}
                retryCount={retryCount}
                maxRetries={3}
              />
            ) : isLoading ? (
              <MatchListSkeleton count={3} />
            ) : matches.length === 0 ? (
              <div className="text-center py-20 sm:py-24">
                {filter === 'live' ? (
                  <>
                    <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-muted/50 mb-5">
                      <Zap className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">No live matches</h3>
                    <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                      There are no matches being played right now. Check back soon!
                    </p>
                  </>
                ) : (
                  <>
                    <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-muted/50 mb-5">
                      <Calendar className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">No matches scheduled</h3>
                    <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                      No matches found for {format(selectedDate, 'MMMM d, yyyy')}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <VirtualizedMatchList
                matches={matches}
                leagues={leagues}
                initialLimit={5}
              />
            )}
          </ErrorBoundary>

          {/* Daily Coupons CTA */}
          <div className="mt-6 pt-6 border-t border-border/50">
            <Link to="/coupons" className="block">
              <Button
                variant="default"
                size="lg"
                className="w-full gap-2 h-12 sm:h-14 text-base font-semibold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-md hover:shadow-lg transition-all"
              >
                <Ticket className="h-5 w-5" />
                <span>Daily Coupons</span>
                <span className="text-sm opacity-80 hidden sm:inline">– AI Combo Picks</span>
              </Button>
            </Link>
          </div>

          {/* Global CTA Buttons */}
          <GlobalCTAButtons className="mt-4" />
        </section>
      </main>
      
      <Footer />

      {/* How it works modal */}
      <HowItWorksModal open={showHowItWorks} onOpenChange={setShowHowItWorks} />
    </div>
  );
}
